import { type ReactNode, useEffect, useRef } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import {
  LayoutDashboard, Building2, Users, Calendar, Briefcase, FileText, Heart, Network, Settings, ArrowLeft, Newspaper, Database,
} from 'lucide-react'
import type { SharedProps } from '@/types'

const navItems = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { href: '/admin/organizations', label: 'Organizations', icon: Building2, exact: false },
  { href: '/admin/users', label: 'Users', icon: Users, exact: false },
  { href: '/admin/events', label: 'Events', icon: Calendar, exact: false },
  { href: '/admin/event_services', label: 'Event Services', icon: Briefcase, exact: false },
  { href: '/admin/causes', label: 'Causes', icon: Heart, exact: false },
  { href: '/admin/professional_networks', label: 'Professional Networks', icon: Network, exact: false },
  { href: '/admin/pages', label: 'Pages', icon: FileText, exact: false },
  { href: '/admin/blog_posts', label: 'Blog', icon: Newspaper, exact: false },
  { href: '/admin/research', label: 'Research', icon: Database, exact: false },
  { href: '/admin/settings', label: 'Settings', icon: Settings, exact: false },
]

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { flash } = usePage<SharedProps>().props
  const url = usePage().url
  const flashRef = useRef<string | null>(null)

  useEffect(() => {
    const flashKey = JSON.stringify(flash)
    if (flashKey === flashRef.current) return
    flashRef.current = flashKey
    if (flash.success) toast.success(flash.success)
    if (flash.alert) toast.error(flash.alert)
    if (flash.notice) toast.info(flash.notice)
  }, [flash])

  function isActive(href: string, exact: boolean) {
    if (exact) return url === href
    return url.startsWith(href)
  }

  return (
    <div className="flex min-h-screen">
      <Toaster position="top-right" richColors />
      <aside className="w-64 bg-foreground text-background flex flex-col shrink-0">
        <div className="p-4 border-b border-white/10">
          <p className="text-base font-semibold tracking-tight">Gala Season Admin</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const active = isActive(item.href, item.exact)
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-base ${
                  active ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="size-4" />
                {item.label}
              </Link>
            )
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Link
            href="/"
            className="flex items-center gap-2 text-base text-white/60 hover:text-white"
          >
            <ArrowLeft className="size-4" />
            Back to site
          </Link>
        </div>
      </aside>
      <main className="flex-1 bg-background p-8">
        {children}
      </main>
    </div>
  )
}
